if ( typeof InfiniteScroll !== 'function' ) {

  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        productGrid: '#main-collection-product-grid',
        productGridItems: '#main-collection-product-grid > *',
        productItem: '.product-item[id^="product-item-"]',
        quickView: 'quick-view-product',
        sentinel: '[data-js-infinite-scroll-sentinel]',
        loadingIndicator: '[data-js-infinite-scroll-loading]',
        pageItems: '[data-page]',
        substituteProduct: '[data-substituted-product]'
      };
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    init() {
      this.collectionHandle = this.dataset.collectionHandle;
      this.initialPage = parseInt(this.dataset.currentPage) || 1;
      this.currentPage = this.initialPage;
      this.totalPages = parseInt(this.dataset.totalPages) || 1;
      this.sectionId = this.dataset.sectionId;
      this.storageKey = 'infiniteScroll_lastProduct';

      this.loading = false;
      this.loadingPrevious = false;
      this.allNextPagesLoaded = this.currentPage >= this.totalPages;
      this.allPreviousPagesLoaded = this.initialPage <= 1;
      this.loadedPages = [this.currentPage];
      this.lowestLoadedPage = this.initialPage;
      this.abortController = null;

      this.cacheDOM();
      this.setupProductClickListener();
      this.handleScrollRestoration();
      this.setupProductObserver();
      this.setupScrollDirectionDetector();

      if (this.totalPages <= 1 && this.initialPage <= 1) return;

      this.setupObserver();
    }

    cleanup() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.productObserver) {
        this.productObserver.disconnect();
        this.productObserver = null;
      }

      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }

      if (this.productClickHandler && this.productGrid) {
        this.productGrid.removeEventListener('click', this.productClickHandler);
        this.productClickHandler = null;
      }

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      this.productGrid = null;
      this.sentinel = null;
      this.loadingIndicator = null;
    }

    cacheDOM() {
      this.productGrid = document.querySelector(this.selectors.productGrid);
      this.sentinel = this.querySelector(this.selectors.sentinel);
      this.loadingIndicator = this.querySelector(this.selectors.loadingIndicator);
    }

    setupObserver() {
      if (!this.sentinel) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.loading && !this.allNextPagesLoaded) {
            this.loadNextPage();
          }
        });
      }, {
        root: null,
        rootMargin: '200px',
        threshold: 0.1
      });

      this.observer.observe(this.sentinel);
    }

    async fetchPageProducts(page, options = {}) {
      const urlParams = new URLSearchParams(window.location.search);

      urlParams.set('page', page);
      urlParams.set('section_id', this.sectionId);

      const rootPath = window.Shopify?.routes?.root || '/';
      const url = `${rootPath}collections/${this.collectionHandle}?${urlParams.toString()}`;
      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      return Array.from(doc.querySelectorAll(this.selectors.productGridItems));
    }

    async loadNextPage() {
      if (this.loading || this.allNextPagesLoaded) return;

      this.loading = true;
      this.showLoading();

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      try {
        const nextPage = this.currentPage + 1;
        const newProducts = await this.fetchPageProducts(nextPage, {
          signal: this.abortController.signal
        });

        if (newProducts.length > 0) {
          newProducts.forEach(product => this.productGrid.appendChild(product));

          this.currentPage = nextPage;
          this.loadedPages.push(nextPage);
          this.observeProducts();

          if (this.currentPage >= this.totalPages) {
            this.allNextPagesLoaded = true;
            this.observer.disconnect();
            
            if (this.allNextPagesLoaded) this.appendSubstitutedProduct();
          }
        } else {
          this.allNextPagesLoaded = true;
          this.observer.disconnect();
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error loading next page:', error);
      } finally {
        this.loading = false;
        this.hideLoading();
        this.abortController = null;
      }
    }

    async loadPreviousPage() {
      if (this.loadingPrevious || this.allPreviousPagesLoaded || this.lowestLoadedPage <= 1) return;

      this.loadingPrevious = true;

      try {
        const prevPage = this.lowestLoadedPage - 1;
        const newProducts = await this.fetchPageProducts(prevPage);

        if (newProducts.length > 0) {
          const fragment = document.createDocumentFragment();

          newProducts.forEach(product => fragment.appendChild(product));

          const scrollY = window.scrollY;
          const gridHeightBefore = this.productGrid.offsetHeight;

          this.productGrid.insertBefore(fragment, this.productGrid.firstChild);

          const heightDiff = this.productGrid.offsetHeight - gridHeightBefore;

          window.scrollTo({ top: scrollY + heightDiff, behavior: 'instant' });

          this.lowestLoadedPage = prevPage;
          this.loadedPages.unshift(prevPage);
          this.observeProducts();

          if (prevPage <= 1) {
            this.allPreviousPagesLoaded = true;
            this.removeScrollHandler();
          }
        } else {
          this.allPreviousPagesLoaded = true;
          this.removeScrollHandler();
        }
      } catch (error) {
        console.error('Error loading previous page:', error);
      } finally {
        this.loadingPrevious = false;
      }
    }

    showLoading() {
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = 'block';
      }
    }

    hideLoading() {
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = 'none';
      }
    }

    updateUrlWithPage(page) {
      const url = new URL(window.location.href);

      if (page <= 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', page);
      }

      window.history.replaceState({ page }, '', url.toString());
    }

    setupProductObserver() {
      if (!this.productGrid) return;

      this.visiblePages = new Set();

      this.productObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const page = entry.target.dataset.page;

          if (!page) return;

          if (entry.isIntersecting) {
            this.visiblePages.add(page);
          } else {
            this.visiblePages.delete(page);
          }
        });

        this.updateUrlFromVisibleProducts();
      }, {
        root: null,
        rootMargin: '0px',
        threshold: 0
      });

      this.observeProducts();
    }

    observeProducts() {
      if (!this.productObserver || !this.productGrid) return;

      const products = this.productGrid.querySelectorAll(this.selectors.pageItems);

      products.forEach(product => {
        this.productObserver.observe(product);
      });
    }

    updateUrlFromVisibleProducts() {
      if (this.visiblePages.size === 0) return;

      const minPage = Math.min(...Array.from(this.visiblePages).map(Number));
      const currentUrlPage = new URLSearchParams(window.location.search).get('page');

      if (currentUrlPage !== String(minPage)) {
        this.updateUrlWithPage(minPage);
      }
    }

    setupScrollDirectionDetector() {
      if (this.allPreviousPagesLoaded) return;

      this.lastScrollY = window.scrollY;

      this.scrollHandler = () => {
        const currentScrollY = window.scrollY;
        const isScrollingUp = currentScrollY < this.lastScrollY;

        if (isScrollingUp && !this.allPreviousPagesLoaded && !this.loadingPrevious) {
          this.loadPreviousPage();
        }

        this.lastScrollY = currentScrollY;
      };

      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    removeScrollHandler() {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
    }

    appendSubstitutedProduct() {
      const scriptTag = document.querySelector(this.selectors.substituteProduct);

      if (!scriptTag || scriptTag.textContent.trim() === 'null') return;

      const substitutedProductHtml = JSON.parse(scriptTag.textContent);

      if (substitutedProductHtml) {
        this.productGrid.insertAdjacentHTML('beforeend', substitutedProductHtml);
        scriptTag.remove();
      }
    }

    setupProductClickListener() {
      if (!this.productGrid) return;

      this.productClickHandler = (e) => {
        const link = e.target.closest('a[href]');

        if (!link) return;

        const href = link.href;

        if (!href || !href.includes('/products/')) return;

        if (link.closest(this.selectors.quickView)) return;

        const productItem = link.closest(this.selectors.productItem);

        if (!productItem) return;

        const productId = productItem.id;

        sessionStorage.setItem(this.storageKey, JSON.stringify({
          productId,
          collectionHandle: this.collectionHandle
        }));
      };

      this.productGrid.addEventListener('click', this.productClickHandler);
    }

    handleScrollRestoration() {
      if (!this.productGrid) return;

      const targetElement = this.getScrollTarget();

      if (!targetElement) return;
      
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }

      this.scrollToElement(targetElement.element, targetElement.block);
    }

    getScrollTarget() {
      const saved = sessionStorage.getItem(this.storageKey);

      if (saved) {
        try {
          const { productId, collectionHandle } = JSON.parse(saved);

          sessionStorage.removeItem(this.storageKey);

          if (collectionHandle !== this.collectionHandle) return null;

          const navEntry = performance.getEntriesByType('navigation')[0];
          const isBackForward = navEntry && navEntry.type === 'back_forward';

          if (!isBackForward) return null;

          const product = document.getElementById(productId);

          if (product) {
            return { element: product, block: 'center' };
          }
        } catch (e) {
          sessionStorage.removeItem(this.storageKey);
        }
      }

      if (this.initialPage > 1) {
        const firstProduct = this.productGrid.querySelector(this.selectors.pageItems);

        if (firstProduct) {
          return { element: firstProduct, block: 'start' };
        }
      }

      return null;
    }

    scrollToElement(element, block = 'start') {
      requestAnimationFrame(() => {
        element.scrollIntoView({ behavior: 'instant', block });
      });
    }
  }

  if ( typeof customElements.get('infinite-scroll') == 'undefined' ) {
    customElements.define('infinite-scroll', InfiniteScroll);
  }
}